import { Trans, useTranslation } from 'next-i18next';

import { AVATAR_SM, CONTAINER, EYEBROW, EYEBROW_MARK, H2, LEAD, SECTION_HEAD, SECTION_PAD } from '../classes';
import { Avatar } from './hero';

interface Testimonial {
  key: 'coldzera' | 'sacy' | 'suja';
  name: string;
  socials: Array<{ label: string; href: string }>;
}

const TESTIMONIALS: Testimonial[] = [
  {
    key: 'coldzera',
    name: 'coldzera',
    socials: [
      { label: 'Instagram', href: 'https://www.instagram.com/coldzera/' },
      { label: 'X', href: 'https://x.com/coldzera' },
      { label: 'YouTube', href: 'https://www.youtube.com/@Coldzera' },
      { label: 'Twitch', href: 'https://www.twitch.tv/coldzin' },
    ],
  },
  {
    key: 'sacy',
    name: 'Sacy',
    socials: [
      { label: 'Instagram', href: 'https://www.instagram.com/sacyrossi/' },
      { label: 'X', href: 'https://x.com/gustavosacy' },
      { label: 'TikTok', href: 'https://www.tiktok.com/@sacy_rossi' },
      { label: 'YouTube', href: 'https://www.youtube.com/c/Sacyadc' },
      { label: 'Twitch', href: 'https://www.twitch.tv/sacy' },
    ],
  },
  {
    key: 'suja',
    name: 'Suja',
    socials: [
      { label: 'Instagram', href: 'https://www.instagram.com/suja_gg/' },
      { label: 'X', href: 'https://x.com/suja_gg' },
      { label: 'TikTok', href: 'https://www.tiktok.com/@suja_gg' },
      { label: 'YouTube', href: 'https://www.youtube.com/SUJAYoutube' },
      { label: 'Twitch', href: 'https://www.twitch.tv/suja' },
    ],
  },
];

const ROSTER = [
  { slug: 'coldzera', name: 'coldzera' },
  { slug: 'sacy', name: 'Sacy' },
  { slug: 'suja', name: 'Suja' },
  { slug: 'arkane', name: 'Arkane' },
  { slug: 'lestripez', name: 'Lestripez' },
];

export function Pros() {
  const { t } = useTranslation('pages/trial-matrix');

  return (
    <section className={SECTION_PAD} id='pros' aria-labelledby='pros-t'>
      <div className={CONTAINER}>
        <div className={SECTION_HEAD} data-reveal=''>
          <p className={EYEBROW}>
            <span className={EYEBROW_MARK} aria-hidden='true'>
              &gt;
            </span>{' '}
            {t('lp.pros.eyebrow')}
          </p>
          <h2 className={H2} id='pros-t'>
            <Trans t={t} i18nKey='lp.pros.title' components={{ em: <em /> }} />
          </h2>
          <p className={LEAD}>{t('lp.pros.lead')}</p>
        </div>

        <div className='my-8 grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-4'>
          {TESTIMONIALS.map((testimonial) => (
            <article key={testimonial.key} className='rounded-2xl border border-[rgb(110_226_220/.32)] bg-[rgb(9_26_43/.82)] p-5 text-[#f3fbff] shadow-[0_15px_38px_rgb(0_0_0/.12)] sm:p-[1.35rem] forced-colors:border-[CanvasText]' data-reveal=''>
              <blockquote className='m-0 mb-4 text-pretty text-[1rem] leading-[1.6]'>{t(`lp.pros.${testimonial.key}Quote`)}</blockquote>
              <strong className='block text-[1.05rem] text-[#85ece1]'>{testimonial.name}</strong>
              <small className='mb-[.7rem] mt-[.15rem] block text-[.8125rem] opacity-[.72]'>{t(`lp.pros.${testimonial.key}Role`)}</small>
              <nav className='-mx-1 flex flex-wrap' aria-label={t('lp.pros.socialLabel', { name: testimonial.name })}>
                {testimonial.socials.map((social) => (
                  <a key={social.label} className='inline-flex min-h-11 min-w-11 items-center justify-center px-[3px] text-[.75rem] leading-[1.3333] text-[#a6eaf2] underline underline-offset-[3px] hover:text-[#93FF18]' href={social.href} target='_blank' rel='noopener noreferrer'>
                    {social.label}
                  </a>
                ))}
              </nav>
            </article>
          ))}
        </div>

        <div className='mt-[clamp(1.25rem,1rem+1vw,1.75rem)] flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-[#2C3B31] bg-[rgb(12_18_13/.7)] px-5 py-4 forced-colors:border-[CanvasText]' data-reveal=''>
          <ul className='flex' aria-label={t('lp.pros.avatarsLabel')}>
            {ROSTER.map((player) => (
              <Avatar key={player.slug} slug={player.slug} name={player.name} size={28} className={AVATAR_SM} />
            ))}
          </ul>
          <p className='text-[.875rem] leading-[1.4286] tracking-[.12em] text-[#93FF18] [text-shadow:0_0_14px_rgb(147_255_24/.5)]' aria-hidden='true'>
            ★★★★★
          </p>
          <p className='min-w-0 flex-[1_1_16rem] text-[.875rem] leading-[1.45] text-[#9AA3B5] max-sm:text-[.9375rem] [&_strong]:font-bold [&_strong]:text-white'>
            <Trans t={t} i18nKey='lp.pros.rating' components={{ strong: <strong /> }} />
          </p>
        </div>
      </div>
    </section>
  );
}
